import { useEffect, useState } from 'react';

import { KYC_FIELDS_SCREEN, KYC_INFO_SCREEN } from 'apps/auth/constants/ComponentNames';
import { KYC_MY_ID_UZ } from 'apps/auth/constants/UrlConstants';
import { apiFetchData } from 'apps/auth/helpers/api';
import { logWarn } from 'apps/auth/helpers/clientLogger';

const SUCCESS = 'success';
const FAILED = 'failed';
const INTRO = 'intro';
const STOP = 'stop';

const makeRandomString = (length) => {
    let result = '';
    const words = 'abdehkmnpswxzABDEFGHKMNPQRSTWXZ123456789';
    const max_position = words.length - 1;
    for (let i = 0; i < length; i++) {
        const pos = Math.floor(Math.random() * max_position);
        result = result + words.substring(pos, pos + 1);
    }
    return result;
};

const makeUrlWithSearchParams = (url, paramsObj) => {
    const searchParams = new URLSearchParams(paramsObj);
    return `${url}?${searchParams.toString()}`;
};

const makeMyIdUrl = (func1, func2) => {
    return (path, clientId, origin) => {
        let url = new URL(path);
        const params = {
            client_id: clientId,
            response_type: 'code',
            redirect_uri: `${origin}${KYC_MY_ID_UZ}`,
            scope: 'address,contacts,doc_data,common_data',
            method: 'strong',
            state: func1(9),
        };
        return func2(url, params);
    };
};
export const checkFields = (fields) => {
    if (fields && (Array.isArray(fields) || Object.keys(fields).length > 0)) {
        return fields;
    }
    return [];
};
const setPassportInput = (fields, comment, placeholder) => {
    const fieldsArray = Array.isArray(fields) ? fields : Object.values(fields);
    const passportField = fieldsArray?.length > 0 && fieldsArray.find((f) => f.name === 'passport');
    const residencyField = fieldsArray?.length > 0 && fieldsArray.find((f) => f.name === 'residency');
    if (passportField && residencyField && comment && placeholder) {
        const value = residencyField.value ?? 'resident';
        const helper = {
            resident: {
                comment: passportField.comment,
                placeholder: passportField.placeholder,
            },
            nonResident: {
                comment,
                placeholder,
            },
        };
        return [residencyField, { ...passportField, comment: helper[value]?.comment, placeholder: helper[value]?.placeholder }];
    }
    return fieldsArray;
};
const prepareValueForServer = (fields, kycRequestId) => {
    return Object.entries(fields).reduce(
        (newFields, [key, field]) => {
            if (Object.hasOwn(field, 'items') && Array.isArray(field.items) && field.items.length > 0 && key.startsWith('transactions')) {
                const selectItem = field.items.find((item) => item.value === field.value);
                newFields[key] = { ...field, value: selectItem ? +selectItem.order + 1 : field.value };
            } else {
                newFields[key] = field;
            }
            return newFields;
        },
        {
            kycRequestId: {
                value: kycRequestId,
                canonicalPath: 'toValues[kycRequestId]',
            },
        },
    );
};

const getQueryParams = () => {
    let myIdCode = null,
        origin = '';
    if (__isBrowser__) {
        const url = new URL(window.location);
        myIdCode = url.searchParams.get('code');
        origin = url.origin;
    }
    return { myIdCode, origin };
};

const useMyIdKycSetScreens = (code, isSuccessCheck = false, isShowFieldsScreen = false) => {
    const [state, setState] = useState({
        screen: '',
        info: '',
    });
    const { screen, info } = state;
    useEffect(() => {
        if (code && isSuccessCheck) {
            setState((prevState) => {
                return {
                    ...prevState,
                    screen: SUCCESS,
                };
            });
        } else if (code && !isSuccessCheck) {
            setState({
                screen: KYC_INFO_SCREEN,
                info: FAILED,
            });
        } else if (isShowFieldsScreen) {
            setState((prevState) => {
                return {
                    ...prevState,
                    screen: KYC_FIELDS_SCREEN,
                };
            });
        } else {
            setState({
                screen: KYC_INFO_SCREEN,
                info: INTRO,
            });
        }
    }, [isSuccessCheck, code, isShowFieldsScreen]);
    return { screen, info, setState };
};

const sendingFieldsToServer = (toggleSpinner, dispatch) => {
    return (url, fields, successFunc, failedFunc) => {
        if (fields && Object.keys(fields).length > 0) {
            toggleSpinner(true)(dispatch);
            apiFetchData(url, fields)
                .then((json) => {
                    if (json) {
                        successFunc(json);
                    } else {
                        throw new Error('Fields have not been sent');
                    }
                })
                .catch((e) => {
                    logWarn(e);
                    failedFunc();
                })
                .finally(() => toggleSpinner(false)(dispatch));
        }
    };
};
const checkValueAndError = (f) => f.value && !f.isError;
const checkResidencyAndError = (f) => f.value === 'nonResident' && !f.isError;

export {
    makeMyIdUrl,
    makeRandomString,
    makeUrlWithSearchParams,
    getQueryParams,
    useMyIdKycSetScreens,
    sendingFieldsToServer,
    checkValueAndError,
    checkResidencyAndError,
    prepareValueForServer,
    setPassportInput,
    FAILED,
    INTRO,
    SUCCESS,
    STOP,
};
